class HoliwoodReviewsV4 {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('[data-hwv4-track]');
    this.prev = section.querySelector('[data-hwv4-prev]');
    this.next = section.querySelector('[data-hwv4-next]');
    this.progress = section.querySelector('[data-hwv4-progress]');
    this.reviewModal = section.querySelector('[data-hwv4-review-modal]');
    this.lightbox = section.querySelector('[data-hwv4-lightbox]');
    this.lightboxImage = section.querySelector('[data-hwv4-lightbox-image]');
    this.photoButtons = [...section.querySelectorAll('[data-hwv4-photo]')];
    this.photoIndex = 0;
    this.timer = null;

    if (!this.track) return;

    this.bindCarousel();
    this.bindReviewModal();
    this.bindLightbox();
    this.equalizeCards();
    this.updateProgress();
    this.startAutoplay();

    this.resizeObserver = new ResizeObserver(() => {
      this.equalizeCards();
      this.updateProgress();
    });

    this.resizeObserver.observe(this.track);
  }

  getScrollStep() {
    const card = this.track.querySelector('[data-hwv4-card]');
    if (!card) return this.track.clientWidth;

    const style = getComputedStyle(this.track);
    const gap = parseFloat(style.gap || 0);
    const cardWidth = card.getBoundingClientRect().width;

    if ((this.section.dataset.scrollMode || 'page') === 'card') {
      return cardWidth + gap;
    }

    const visibleCards = Math.max(
      1,
      Math.round(this.track.clientWidth / (cardWidth + gap))
    );

    return visibleCards * (cardWidth + gap);
  }

  scroll(direction) {
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const atStart = this.track.scrollLeft <= 4;
    const atEnd = this.track.scrollLeft >= maxScroll - 4;

    if (direction > 0 && atEnd) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    if (direction < 0 && atStart) {
      this.track.scrollTo({ left: maxScroll, behavior: 'smooth' });
      return;
    }

    this.track.scrollBy({
      left: this.getScrollStep() * direction,
      behavior: 'smooth'
    });
  }

  bindCarousel() {
    this.prev?.addEventListener('click', () => {
      this.scroll(-1);
      this.restartAutoplay();
    });

    this.next?.addEventListener('click', () => {
      this.scroll(1);
      this.restartAutoplay();
    });

    this.track.addEventListener(
      'scroll',
      () => requestAnimationFrame(() => this.updateProgress()),
      { passive: true }
    );

    this.track.addEventListener('focusin', () => this.stopAutoplay());
    this.track.addEventListener('focusout', () => this.startAutoplay());
  }

  equalizeCards() {
    if (!this.section.classList.contains('hw-review-v4--height-equal')) {
      this.section.style.removeProperty('--hwv4-equal-height');
      return;
    }

    const cards = [...this.track.querySelectorAll('[data-hwv4-card]')];
    cards.forEach(card => {
      card.style.height = 'auto';
    });

    const tallest = Math.max(...cards.map(card => card.scrollHeight), 0);
    this.section.style.setProperty('--hwv4-equal-height', `${tallest}px`);
  }

  updateProgress() {
    if (!this.progress) return;

    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const visibleRatio = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
    const travel = 100 - visibleRatio * 100;
    const position = maxScroll > 0
      ? (this.track.scrollLeft / maxScroll) * travel
      : 0;

    this.progress.style.width = `${visibleRatio * 100}%`;
    this.progress.style.transform = `translateX(${position}%)`;
  }

  startAutoplay() {
    if (this.section.dataset.autoplay !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this.timer) return;

    const delay = Number(this.section.dataset.autoplayDelay) || 6000;
    this.timer = window.setInterval(() => this.scroll(1), delay);
  }

  stopAutoplay() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  bindReviewModal() {
    if (!this.reviewModal) return;

    const closeButton = this.reviewModal.querySelector('[data-hwv4-review-close]');
    const stars = this.reviewModal.querySelector('[data-hwv4-modal-stars]');
    const title = this.reviewModal.querySelector('[data-hwv4-modal-title]');
    const body = this.reviewModal.querySelector('[data-hwv4-modal-body]');
    const product = this.reviewModal.querySelector('[data-hwv4-modal-product]');
    const customer = this.reviewModal.querySelector('[data-hwv4-modal-customer]');
    const date = this.reviewModal.querySelector('[data-hwv4-modal-date]');

    this.section.querySelectorAll('[data-hwv4-read-more]').forEach(button => {
      button.addEventListener('click', () => {
        const card = button.closest('[data-hwv4-card]');
        if (!card) return;

        const rating = Math.max(
          0,
          Math.min(5, Number(card.dataset.rating) || 0)
        );

        stars.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
        title.textContent = card.dataset.title || '';
        body.textContent = card.dataset.body || '';
        customer.textContent = card.dataset.customer
          ? `— ${card.dataset.customer}`
          : '';
        date.textContent = card.dataset.date || '';

        const productTitle = card.dataset.productTitle || '';
        product.textContent = productTitle;
        product.href = card.dataset.productUrl || '#';
        product.hidden = !productTitle;

        this.openDialog(this.reviewModal);
      });
    });

    closeButton?.addEventListener('click', () => {
      this.closeDialog(this.reviewModal);
    });

    this.reviewModal.addEventListener('click', event => {
      if (event.target === this.reviewModal) {
        this.closeDialog(this.reviewModal);
      }
    });
  }

  bindLightbox() {
    if (!this.lightbox || !this.photoButtons.length) return;

    this.photoButtons.forEach((button, index) => {
      button.addEventListener('click', () => {
        this.photoIndex = index;
        this.renderPhoto();
        this.openDialog(this.lightbox);
      });
    });

    this.lightbox
      .querySelector('[data-hwv4-lightbox-close]')
      ?.addEventListener('click', () => this.closeDialog(this.lightbox));

    this.lightbox
      .querySelector('[data-hwv4-lightbox-prev]')
      ?.addEventListener('click', () => this.changePhoto(-1));

    this.lightbox
      .querySelector('[data-hwv4-lightbox-next]')
      ?.addEventListener('click', () => this.changePhoto(1));

    this.lightbox.addEventListener('click', event => {
      if (event.target === this.lightbox) {
        this.closeDialog(this.lightbox);
      }
    });

    this.lightbox.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') this.changePhoto(-1);
      if (event.key === 'ArrowRight') this.changePhoto(1);
    });
  }

  changePhoto(direction) {
    this.photoIndex =
      (this.photoIndex + direction + this.photoButtons.length) %
      this.photoButtons.length;

    this.renderPhoto();
  }

  renderPhoto() {
    const button = this.photoButtons[this.photoIndex];
    if (!button || !this.lightboxImage) return;

    this.lightboxImage.src = button.dataset.src || '';
    this.lightboxImage.alt =
      button.dataset.alt || 'Customer project photo';
  }

  openDialog(dialog) {
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  closeDialog(dialog) {
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  destroy() {
    this.stopAutoplay();
    this.resizeObserver?.disconnect();
  }
}

const hwv4Instances = new WeakMap();

function initHwv4(root = document) {
  root.querySelectorAll('[data-hw-review-v4]').forEach(section => {
    if (hwv4Instances.has(section)) return;

    hwv4Instances.set(
      section,
      new HoliwoodReviewsV4(section)
    );
  });
}

document.addEventListener('DOMContentLoaded', () => initHwv4());

document.addEventListener('shopify:section:load', event => {
  initHwv4(event.target);
});

document.addEventListener('shopify:section:unload', event => {
  const section = event.target.querySelector('[data-hw-review-v4]');
  hwv4Instances.get(section)?.destroy();
});
